import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Projects from './pages/Projects';
import Orders from './pages/Orders';
import OrderItems from './pages/OrderItems';
import PendingDeliveries from './pages/PendingDeliveries';
import Vendors from './pages/Vendors';
import Products from './pages/Products';
import Inventory from './pages/Inventory';
import Profit from './pages/FinancialDashboard';
import Payments from './pages/Payments';
import Returns from './pages/Returns';
import Expenses from './pages/Expenses';
import MyProjects from './pages/MyProjects';
import MyOrders from './pages/MyOrders';
import VendorOrders from './pages/VendorOrders';
import VendorProducts from './pages/VendorProducts';
import UserManagement from './pages/UserManagement';
import PurchaseOrders from './pages/PurchaseOrders';
import QuotationBuilder from './pages/QuotationBuilder';
import BusinessSettings from './pages/BusinessSettings';
import Leads from './pages/Leads';
import Partners from './pages/Partners';
import Ledger from './pages/Ledger';
import ClientQuotation from './pages/ClientQuotation';
import Portfolio from './pages/Portfolio';
import PortfolioAdmin from './pages/PortfolioAdmin';
import ProjectStatement from './pages/ProjectStatement';
import Audit from './pages/Audit';
import StoreOrders from './pages/StoreOrders';
import StoreHome from './pages/store/Home';
import Shop from './pages/store/Shop';
import ProductDetail from './pages/store/ProductDetail';
import Cart from './pages/store/Cart';
import Checkout from './pages/store/Checkout';
import OrderTracking from './pages/store/OrderTracking';
import { CartProvider } from './contexts/CartContext';
import DashboardLayout from './components/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ProjectTasks from './pages/ProjectTasks';
import TaskTemplates from './pages/TaskTemplates';
import UpcomingTasks from './pages/UpcomingTasks';
import './App.css';

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/get-quote" element={<ClientQuotation />} />
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/projects/:projectId/statement" element={<ProjectStatement />} />
      {/* Public storefront */}
      <Route path="/shop" element={<Shop />} />
      <Route path="/product/:productId" element={<ProductDetail />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/order/:token" element={<OrderTracking />} />
      {/* Public home */}
      <Route path="/" element={<StoreHome />} />
      {/* Admin app (auth required) — reachable only via /admin/* now */}
      <Route path="/admin" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:projectId/tasks" element={<ProjectTasks />} />
        <Route path="task-templates" element={<TaskTemplates />} />
        <Route path="upcoming-tasks" element={<UpcomingTasks />} />
        <Route path="orders" element={<Orders />} />
        <Route path="order-items/:orderId" element={<OrderItems />} />
        <Route path="pending-deliveries" element={<PendingDeliveries />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="products" element={<Products />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="profit" element={<Profit />} />
        <Route path="payments" element={<Payments />} />
        <Route path="returns" element={<Returns />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="my-projects" element={<MyProjects />} />
        <Route path="my-orders" element={<MyOrders />} />
        <Route path="vendor-orders" element={<VendorOrders />} />
        <Route path="vendor-products" element={<VendorProducts />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="quotation-builder" element={<QuotationBuilder />} />
        <Route path="business-settings" element={<BusinessSettings />} />
        <Route path="leads" element={<Leads />} />
        <Route path="partners" element={<Partners />} />
        <Route path="ledger" element={<Ledger />} />
        <Route path="portfolio-admin" element={<PortfolioAdmin />} />
        <Route path="store-orders" element={<StoreOrders />} />
        <Route path="audit" element={<Audit />} />
      </Route>
      {/* Legacy admin URLs redirect to /admin */}
      <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <AppRouter />
        </CartProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;